import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchMedia, uploadMedia, fmtBytes, MediaItem } from '../pages/MediaLibrary';

// Media/URL field for flow-builder image/document/video/audio steps.
// Keeps the existing "paste a URL" behavior and adds a "Media Library"
// mode so users without their own hosting can upload straight from here.
interface Props {
  value: string;
  onChange: (url: string) => void;
  kind?: 'image' | 'document' | 'video' | 'audio';
  placeholder?: string;
}

const KIND_ICONS: Record<string, string> = { image: '🖼️', document: '📄', video: '🎬', audio: '🎵' };

const S = {
  input: { width: '100%', padding: '9px 12px', background: 'var(--bg-page)', border: '1px solid var(--bg-element)', borderRadius: '8px', color: 'var(--text-primary)', fontSize: '13px', outline: 'none', boxSizing: 'border-box' as const },
  tab: (active: boolean): React.CSSProperties => ({
    padding: '6px 12px', fontSize: '12px', fontWeight: 600, borderRadius: '7px', cursor: 'pointer',
    background: active ? '#5B4FE8' : 'var(--bg-element)', color: active ? 'white' : 'var(--text-secondary)', border: 'none',
  }),
  btnGhost: { padding: '6px 12px', background: 'var(--bg-element)', color: 'var(--text-secondary)', border: '1px solid var(--bg-element)', borderRadius: '7px', cursor: 'pointer', fontSize: '12px' } as React.CSSProperties,
  btnPrimary: { padding: '8px 18px', background: '#5B4FE8', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' } as React.CSSProperties,
};

export default function MediaPicker({ value, onChange, kind = 'image', placeholder = 'URL' }: Props) {
  const [mode, setMode] = useState<'url' | 'library'>('url');
  const [showBrowser, setShowBrowser] = useState(false);
  const [items, setItems] = useState<MediaItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  const openBrowser = async () => {
    setShowBrowser(true);
    setLoading(true);
    setError('');
    try {
      const { items } = await fetchMedia();
      setItems(items.filter(i => i.kind === kind));
    } catch (e: any) {
      setError(e.message || 'Could not load media library.');
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (file: File | null) => {
    if (!file) return;
    setUploading(true);
    setError('');
    try {
      const uploaded = await uploadMedia(file);
      onChange(uploaded.url);
      setMode('library');
      setShowBrowser(false);
    } catch (e: any) {
      setError(e.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const selectItem = (item: MediaItem) => {
    onChange(item.url);
    setMode('library');
    setShowBrowser(false);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      <div style={{ display: 'flex', gap: '6px' }}>
        <button type="button" style={S.tab(mode === 'url')} onClick={() => setMode('url')}>🔗 External URL</button>
        <button type="button" style={S.tab(mode === 'library')} onClick={() => { setMode('library'); openBrowser(); }}>
          {KIND_ICONS[kind]} Media Library
        </button>
      </div>

      {mode === 'url' ? (
        <input value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} style={S.input} />
      ) : (
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <input value={value} readOnly placeholder="No file chosen" style={{ ...S.input, opacity: value ? 1 : 0.6 }} />
          <button type="button" style={S.btnGhost} onClick={openBrowser}>Browse…</button>
        </div>
      )}

      {showBrowser && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}
          onClick={() => setShowBrowser(false)}>
          <div style={{ background: 'var(--bg-card)', borderRadius: '16px', border: '1px solid var(--bg-element)', padding: '24px', maxWidth: '720px', width: '95%', maxHeight: '80vh', overflow: 'auto' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ color: 'var(--text-primary)', fontSize: '15px', fontWeight: 700, margin: 0 }}>Choose from Media Library</h3>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <label style={{ ...S.btnPrimary, cursor: uploading ? 'wait' : 'pointer', opacity: uploading ? 0.6 : 1 }}>
                  {uploading ? 'Uploading…' : '+ Upload new'}
                  <input type="file" style={{ display: 'none' }} disabled={uploading}
                    onChange={e => handleUpload(e.target.files?.[0] || null)} />
                </label>
                <button style={{ background: 'none', border: 'none', color: 'var(--text-faint)', cursor: 'pointer', fontSize: '16px' }} onClick={() => setShowBrowser(false)}>✕</button>
              </div>
            </div>

            {error && <div style={{ color: '#ef4444', fontSize: '12px', marginBottom: '12px' }}>{error}</div>}

            {loading ? (
              <div style={{ color: 'var(--text-muted)', fontSize: '13px', padding: '24px', textAlign: 'center' }}>Loading…</div>
            ) : items.length === 0 ? (
              <div style={{ color: 'var(--text-muted)', fontSize: '13px', padding: '24px', textAlign: 'center' }}>
                No {kind}s in your media library yet. Upload one above.
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '10px' }}>
                {items.map(item => (
                  <div key={item.mediaId} onClick={() => selectItem(item)}
                    style={{ cursor: 'pointer', borderRadius: '10px', border: value === item.url ? '2px solid #5B4FE8' : '1px solid var(--bg-element)', padding: '8px', background: 'var(--bg-page)' }}>
                    <div style={{ height: '80px', borderRadius: '6px', background: 'var(--bg-element)', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', marginBottom: '6px' }}>
                      {item.kind === 'image' ? (
                        <img src={item.url} alt={item.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      ) : (
                        <span style={{ fontSize: '28px' }}>{KIND_ICONS[item.kind]}</span>
                      )}
                    </div>
                    <div style={{ fontSize: '11px', color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.name}</div>
                    <div style={{ fontSize: '10px', color: 'var(--text-faint)' }}>{fmtBytes(item.sizeBytes)}</div>
                  </div>
                ))}
              </div>
            )}

            <div style={{ marginTop: '14px', textAlign: 'center' }}>
              <Link to="/media" target="_blank" style={{ fontSize: '12px', color: '#818cf8', textDecoration: 'none' }}>Manage full media library ↗</Link>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
